import styles from '../CompanyDataPage.module.scss'
import Input from '../../../UI/Input/Input'
import Button from '../../../UI/Button/Button'
import Tooltip from 'react-tooltip-lite'
import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import CompanyLogoEditor from '../../../components/AvatarEditor/CompanyLogoEditor'
import { useFormik } from 'formik'
import { initialValues, validationSchema } from '../config'
import { sendToModeration, sendToReModeration, putCompanyPicture } from '../../../api/putApi'
import { getCompanyProfile } from '../../../redux/actions/company.action'
import { batch, useDispatch } from 'react-redux'
import { setModerUI, setProfileTitle } from '../../../redux/actions/ui.action'
import { getCompaniesUI, getCompanyInfo } from '../../../api/getApi'
import { useNotify } from '../../../hooks/useNotify'

const GeneralForm = ({ isCanEdit, companyData, hideLogoEditor = false, withPageSideEffects = true }) => {
  const { notify } = useNotify()
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [showCropper, setShowCropper] = React.useState(false)
  const [formValues, setFormValues] = useState()
  const [isInputsDisabled, setIsInputsDisabled] = useState(false)

  useEffect(() => {
    setLoading(true)

    if (withPageSideEffects) {
      batch(() => {
        dispatch(setProfileTitle('CrewManager data'))
        dispatch(getCompanyProfile())
      })
    }

    getUi()
    getInfo()
    // eslint-disable-next-line
  }, [withPageSideEffects])

  const formik = useFormik({
    initialValues: formValues ? formValues : initialValues,
    validationSchema,
    enableReinitialize: true,

    onSubmit: async (values) => {
      setShowCropper(false)

      try {
        if (!companyData.on_moderation && !companyData.is_active) {
          if (!companyData.picture.file.includes('company_default.png') && companyData.picture.file.startsWith('data:image')) {
            const base64Data = companyData.picture.file;
            const blob = base64ToBlob(base64Data, values.company_logo.type, values.company_logo.file_name);
            const pictureResponse = await putCompanyPicture(values.company_logo.id, blob, 'company_profile')
            if (pictureResponse) {
              await sendToModeration({
                name: values.name,
                phone: values.phone,
                person_in_charge: values.person_in_charge,
              })
              notify('Success', 'Company sent to moderation successfully.')
            } else {
              notify('Error', 'Failed to upload picture, but proceeding to moderation.')
              await sendToModeration({
                name: values.name,
                phone: values.phone,
                person_in_charge: values.person_in_charge,
              })
            }
          } else {
            await sendToModeration({
              name: values.name,
              phone: values.phone,
              person_in_charge: values.person_in_charge,
            })
            notify('Success', 'Company sent to moderation successfully.')
          }
        } else if (companyData.on_moderation) {
          const remoderationResponse = await sendToReModeration({
            name: values.name,
            phone: values.phone,
            person_in_charge: values.person_in_charge,
          })
          if (remoderationResponse) {

            if (companyData.picture.file.startsWith('data:image')) {
              const base64Data = companyData.picture.file;
              const blob = base64ToBlob(base64Data, values.company_logo.type, values.company_logo.file_name);

              const pictureResponse = await putCompanyPicture(values.company_logo.id, blob, 'company_profile')
              if (pictureResponse) {
                await sendToModeration({
                  name: values.name,
                  phone: values.phone,
                  person_in_charge: values.person_in_charge,
                })
                notify('Success', 'Company sent to moderation successfully after image update.')
              } else {
                notify('Error', 'Failed to upload picture, but proceeding to moderation.')
                await sendToModeration({
                  name: values.name,
                  phone: values.phone,
                  person_in_charge: values.person_in_charge,
                })
              }
            } else {
              await sendToModeration({
                name: values.name,
                phone: values.phone,
                person_in_charge: values.person_in_charge,
              })
              notify('Success', 'Company sent to moderation successfully.')
            }
          } else {
            notify('Error', 'Failed to send to re-moderation.')
          }
        }
      } catch (error) {
        notify.errorsList(error.errors)
      }
    },
  })

  const { values, errors, touched, setValues, setFieldTouched, handleChange, handleSubmit, dirty } = formik
  const hasValidationErrors = Object.keys(errors).length > 0
  const isSubmitDisabled = isInputsDisabled || hasValidationErrors || !dirty
  const shouldShowUnchangedTooltip = !dirty && !isInputsDisabled

  const getUi = async () => {
    try {
      const response = await getCompaniesUI()

      dispatch(setModerUI(response.show_is_active))
    } catch (error) {
      notify.errorsList(error.errors)
    } finally {
      setLoading(false)
    }
  }
  const getInfo = async () => {
    try {
      const response = await getCompanyInfo()
      
      await setValues({
        ...values,
        ...response,
        company_logo: response.picture,
      })

      setFormValues({
        ...values,
        ...response,
        company_logo: response.picture
      })

    } catch (error) {
      notify.errorsList(error.errors)
    } finally {
      setLoading(false)
    }
  }

  const handleChangeFormikValue = (value) => {
    if (!touched[value.target.name]) {
      setFieldTouched(value.target.name, true)
    }

    handleChange(value)
  }

  useEffect(() => {
    setIsInputsDisabled(loading || !isCanEdit)
    //eslint-disable-next-line
  }, [isCanEdit, loading])


  const base64ToBlob = (base64, mimeType, fileName) => {
    const arr = []

    let blobBin = atob(base64.split(',')[1])

    for (let i = 0; i < blobBin.length; i++) {
      arr.push(blobBin.charCodeAt(i))
    }


    return new File([new Uint8Array(arr)], fileName, {
      type: mimeType,
    })
  };

  return (
    <div
      className={cn(styles.company_general, {
        'profile-content space-between': !hideLogoEditor,
        [styles.company_general_singleColumn]: hideLogoEditor,
      })}
    >
      {!hideLogoEditor && (
        <div className={styles.company_general_logo__editor}>
          <CompanyLogoEditor formik={formik} showCropper={showCropper} setShowCropper={setShowCropper} />
        </div>
      )}
      <form className={styles.company_general_form} onSubmit={handleSubmit}>
        <div className="profile-form-main">
          <div className="profile-form-fields">
            <div className="profile-form-column">
              <div className="field-item">
                <Input
                  className={styles.company_data_input}
                  name="email"
                  label="Email"
                  placeholder="Email"
                  role="presentation"
                  autoComplete="off"
                  value={values.email}
                  onChange={handleChangeFormikValue}
                  isInvalid={Boolean(errors.email && touched.email)}
                  errorMessage={errors.email}
                  required
                  disabled
                />
              </div>

              <div className="field-item">
                <Input
                  className={styles.company_data_input}
                  name="name"
                  label="Company Name"
                  placeholder="Company Name"
                  role="presentation"
                  autoComplete="off"
                  value={values.name}
                  onChange={handleChangeFormikValue}
                  isInvalid={Boolean(errors.name && touched.name)}
                  errorMessage={errors.name}
                  disabled={isInputsDisabled}
                  required
                />
              </div>

              <div className="field-item">
                <Input
                  className={styles.company_data_input}
                  name="person_in_charge"
                  label="Person in Charge"
                  placeholder="Person in Charge"
                  role="presentation"
                  autoComplete="off"
                  value={values.person_in_charge}
                  onChange={handleChangeFormikValue}
                  disabled={isInputsDisabled}
                />
              </div>

              <div className="field-item">
                <Input
                  className={styles.company_data_input}
                  name="phone"
                  label="Phone"
                  placeholder="Phone"
                  role="presentation"
                  autoComplete="off"
                  value={values.phone}
                  onChange={handleChangeFormikValue}
                  isInvalid={Boolean(errors.phone && touched.phone)}
                  errorMessage={errors.phone}
                  disabled={isInputsDisabled}
                />
              </div>


              <div className={styles.save_btn_wrapper}>
                {shouldShowUnchangedTooltip ? (
                  <Tooltip
                    arrow
                    direction="down"
                    arrowSize={7}
                    padding={8}
                    content="Change at least one field to send the form for approval."
                  >
                    <div>
                      <Button type="submit" className={styles.save_btn} disabled={isSubmitDisabled}>
                        Save & send to approval
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button type="submit" className={styles.save_btn} disabled={isSubmitDisabled}>
                    Save & send to approval
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
export default GeneralForm
